<template>
  <transition name="timer-slide">
    <button
      v-if="isVisible"
      class="game-timer"
      :class="{
        paused: timer.status === 'paused',
        finished: timer.status === 'finished',
        warning: timer.status === 'running' && remainingSeconds <= 60,
        urgent: timer.status === 'running' && remainingSeconds <= 10,
        controllable: !session.isSpectator,
      }"
      type="button"
      :disabled="session.isSpectator"
      :title="session.isSpectator ? statusText : $t('timer.openControls')"
      :aria-label="`${$t('timer.title')}: ${formattedTime}. ${statusText}`"
      @click="openControls"
    >
      <font-awesome-icon
        class="timer-icon"
        :icon="timer.status === 'paused' ? 'pause' : 'hourglass-half'"
      />
      <span class="timer-time">{{ formattedTime }}</span>
      <span class="timer-status">{{ statusText }}</span>
    </button>
  </transition>
</template>

<script>
import { mapState } from "vuex";

export default {
  computed: {
    timer() {
      return this.session.timer;
    },
    timerStatus() {
      return this.timer.status;
    },
    isVisible() {
      return this.timer.status !== "idle";
    },
    remainingMilliseconds() {
      if (this.timer.status === "running") {
        return Math.max(0, this.timer.endAt - this.now);
      }
      return Math.max(0, this.timer.remainingMs || 0);
    },
    remainingSeconds() {
      return Math.ceil(this.remainingMilliseconds / 1000);
    },
    formattedTime() {
      const minutes = Math.floor(this.remainingSeconds / 60);
      const seconds = this.remainingSeconds % 60;
      return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    },
    statusText() {
      if (this.timer.status === "paused") return this.$t("timer.paused");
      if (this.timer.status === "finished") return this.$t("timer.timeUp");
      return this.$t("timer.remaining");
    },
    ...mapState(["session", "grimoire"]),
  },
  data() {
    return {
      now: Date.now(),
      ticker: null,
    };
  },
  mounted() {
    this.ticker = setInterval(this.tick, 250);
    this.tick();
  },
  beforeDestroy() {
    clearInterval(this.ticker);
  },
  methods: {
    tick() {
      this.now = Date.now();
      if (this.timer.status === "running" && this.timer.endAt <= this.now) {
        this.$store.commit("session/setTimer", {
          ...this.timer,
          status: "finished",
          remainingMs: 0,
          endAt: 0,
          finishedAt: this.now,
        });
      }
    },
    openControls() {
      if (this.session.isSpectator) return;
      this.$store.commit("toggleModal", "timer");
    },
    playAlarm() {
      if (!this.timer.soundEnabled || this.grimoire.isMuted) return;
      const alarm = new Audio(require("@/assets/sounds/roles-notify.mp3"));
      alarm.play().catch(() => {});
    },
  },
  watch: {
    timerStatus(status, previousStatus) {
      if (status === "finished" && previousStatus === "running") {
        this.playAlarm();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.game-timer {
  position: absolute;
  z-index: 74;
  top: 10px;
  left: 50%;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  column-gap: 8px;
  min-width: 126px;
  padding: 7px 14px 6px;
  transform: translateX(-50%);
  color: white;
  background: rgba(0, 0, 0, 0.82);
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 12px;
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.9);
  font: inherit;
  text-shadow: 1px 1px 2px black;

  &:disabled {
    opacity: 1;
  }

  &.controllable {
    cursor: pointer;

    &:hover {
      border-color: #ffd15c;
    }
  }

  &.paused {
    border-color: #ffd15c;
  }

  &.warning {
    color: #ffd15c;
  }

  &.urgent,
  &.finished {
    color: #ff6161;
    border-color: #ce0100;
  }

  &.finished {
    animation: timer-pulse 1s ease-in-out 3;
  }
}

.timer-icon {
  grid-row: 1 / 3;
  align-self: center;
  font-size: 1.2em;
}

.timer-time {
  font-family: monospace;
  font-size: 1.35em;
  font-weight: bold;
  line-height: 1;
  letter-spacing: 1px;
}

.timer-status {
  margin-top: 3px;
  font-size: 0.55em;
  line-height: 1;
  text-transform: uppercase;
}

@keyframes timer-pulse {
  50% {
    transform: translateX(-50%) scale(1.08);
    box-shadow: 0 0 24px rgba(206, 1, 0, 0.9);
  }
}

.timer-slide-enter-active,
.timer-slide-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.timer-slide-enter,
.timer-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, -20px);
}

@media screen and (max-width: 575.98px) {
  .game-timer {
    top: 4px;
    min-width: 108px;
    padding: 5px 10px;
  }
}
</style>
